import { JourneyType as JourneyTypeEnum } from '@bytel/leviator';

import { getAppConfig } from '@services/config';
import { apiHttpService } from '@services/http';
import { CACHE_TAGS } from '@services/http/http';

import { EasyTradeInMapper } from '@mappers/easy-trade-in';

import {
    SapicContextualisedProductPayload,
    SapicFundingMode,
    SapicTradeInRessourceAttribuee,
} from '@app-types/api/sapic';
import type { TradeInSubType } from '@app-types/easy-trade-in';
import { EasyTradeInEligibility, EasyTradeInEligibilityResponse, EasyTradeInResult } from '@app-types/easy-trade-in';
import { UserType } from '@app-types/user';

import { SAPIC_X_VERSION } from '@constants/api';

const appConfig = getAppConfig();

const TRADE_IN_PARAMS = new URLSearchParams({
    modePourFinancement: SapicFundingMode.MIN_ONE_OFF,
    stock: 'tous',
    filtre: 'type="mobile-take-back"',
    detail: '0',
});

export type TradeInEligibilityContext = {
    isRenewal: boolean;
    idPu?: string;
    contractId?: string;
    user?: UserType;
};

function buildBody(
    { isRenewal, idPu, contractId }: TradeInEligibilityContext,
    ressourcesAttribuees?: SapicTradeInRessourceAttribuee[],
): SapicContextualisedProductPayload {
    return {
        referenceExterneParcoursCible: 'idParcours-0',
        panier: {
            acheteur: idPu ? { type: 'PERSONNE', noPersonne: idPu } : { type: 'NON_CONNU_GP' },
            parcours: [
                {
                    referenceExterne: 'idParcours-0',
                    typeParcours: isRenewal ? JourneyTypeEnum.RENEWAL : JourneyTypeEnum.MOBILE_ACQUISITION,
                    statut: 'EN_COURS',
                    ...(contractId && { contrat: { identifiant: contractId } }),
                    elements: [],
                },
            ],
        },
        ...(ressourcesAttribuees && { contextualisation: { ressourcesAttribuees } }),
    };
}

class SapicTradeInRepository {
    private readonly url = `${appConfig.b2r.url}/catalogue/produits-contextualises?${TRADE_IN_PARAMS}`;

    async getTradeInEligibility(context: TradeInEligibilityContext): Promise<EasyTradeInEligibility> {
        const response = await apiHttpService.post<EasyTradeInEligibilityResponse>(this.url, buildBody(context), {
            headers: { 'x-version': SAPIC_X_VERSION.V5 },
            user: context.idPu ? context.user : undefined,
            ttl: parseInt(appConfig.cache.ttl.products),
            tags: [CACHE_TAGS.PRODUCTS],
        });
        return EasyTradeInMapper.apiToEligibility(response);
    }

    async submitTradeIn(
        context: TradeInEligibilityContext,
        partnerRequestId: string,
        productSubtype: TradeInSubType,
    ): Promise<EasyTradeInResult | null> {
        const response = await apiHttpService.post<EasyTradeInEligibilityResponse>(
            this.url,
            buildBody(context, [
                {
                    typeRessource: 'PARTENAIRE',
                    caracteristiques: [
                        {
                            type: 'CARACTERISTIQUE_SIMPLE',
                            typeCaracteristique: 'ID_DEMANDE_PARTENAIRE',
                            valeur: partnerRequestId,
                        },
                    ],
                },
            ]),
            {
                headers: { 'x-version': SAPIC_X_VERSION.V5 },
                user: context.idPu ? context.user : undefined,
                ttl: 0,
                tags: [CACHE_TAGS.PRODUCTS],
            },
        );

        const product = response.produits.find((product) => product.sousType === productSubtype);
        return product ? EasyTradeInMapper.apiToResult(product) : null;
    }
}

export const sapicTradeInRepository = new SapicTradeInRepository();
